import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { LoginPage } from '@/pages/LoginPage';
import { AppShell } from '@/components/layout/AppShell';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RootRedirect } from '@/pages/RootRedirect';
import { InvoiceListPage } from '@/pages/InvoiceListPage';
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage';
import { SalesPage } from '@/pages/SalesPage';
import { SalesReportPage } from '@/pages/SalesReportPage';
import { FiscalLayout } from '@/pages/fiscal/FiscalLayout';
import { FiscalDashboardPage } from '@/pages/fiscal/FiscalDashboardPage';
import { FiscalInvoicesPage } from '@/pages/fiscal/FiscalInvoicesPage';
import { FiscalReceiptsPage } from '@/pages/fiscal/FiscalReceiptsPage';
import { FiscalPrintersPage } from '@/pages/fiscal/FiscalPrintersPage';
import { FiscalSettingsPage } from '@/pages/fiscal/FiscalSettingsPage';
import { CrmLayout } from '@/pages/crm/CrmLayout';
import { LeadQueue } from '@/pages/crm/LeadQueue';
import { AccountList } from '@/pages/crm/AccountList';
import { AccountDetail } from '@/pages/crm/AccountDetail';
import { DealList } from '@/pages/crm/DealList';
import { MagazynLayout } from '@/pages/magazyn/MagazynLayout';
import { PozycjeCatalog } from '@/pages/magazyn/PozycjeCatalog';
import { WzList } from '@/pages/magazyn/WzList';
import { WzEditor } from '@/pages/magazyn/WzEditor';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ContractorsPage } from '@/pages/ContractorsPage';
import { ContractorDetailPage } from '@/pages/ContractorDetailPage';
import { MileagePage } from '@/pages/MileagePage';
import { EmployeesPage } from '@/pages/EmployeesPage';
import { EmployeeDetailPage } from '@/pages/EmployeeDetailPage';
import { EmployeeCostReportPage } from '@/pages/EmployeeCostReportPage';
import { HrPage } from '@/pages/HrPage';
import { HrTeamPage } from '@/pages/HrTeamPage';
import { HrAdminPage } from '@/pages/HrAdminPage';
import { HrMyInvoicesPage } from '@/pages/HrMyInvoicesPage';
import { HrReimbursementsPage } from '@/pages/HrReimbursementsPage';
import { HrMyDataPage } from '@/pages/HrMyDataPage';
import { HrDocumentsPage } from '@/pages/HrDocumentsPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { TemplateEditorPage } from '@/pages/TemplateEditorPage';
import { TagManagement } from '@/components/tags/TagManagement';
import { CompanyManagement } from '@/components/companies/CompanyManagement';
import { UserManagement } from '@/components/users/UserManagement';
import { ApiApplications } from '@/components/admin/ApiApplications';
import { AuthSessionProvider } from '@/context/AuthSessionContext';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { VersionUpdateNotifier } from '@/components/layout/VersionUpdateNotifier';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyAdminRole, hasModuleAccess, landingSectionFor } from '@/lib/permissions';
import { t } from '@/i18n';
import type { PermissionModule } from '@ip/shared-types';
import './App.css';

/**
 * Full-screen spinner shown while a permission-gated route waits for
 * isAuthReady — i.e. MSAL account restoration and /auth/me are still in
 * flight. Rendering this (instead of evaluating the gate against transient
 * "not loaded yet" values) is what prevents the hard-navigation bounce to
 * `/` for admin routes.
 */
function AuthGateSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="text-muted-foreground size-8 animate-spin" />
    </div>
  );
}

/** Renders the gated element once ready; redirects to `/` only after auth has settled AND access is denied. */
function GatedRoute({
  ready,
  allowed,
  children,
}: {
  ready: boolean;
  allowed: boolean;
  children: ReactNode;
}) {
  if (!ready) return <AuthGateSpinner />;
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Permission-aware /:nip index redirect: bare /:nip (e.g. the breadcrumb
 * company link) goes to the first section the user's permissions actually
 * grant — never to a hard-coded 'invoices' a module-restricted user (e.g. a
 * `pracownik` with only 'hr') would be rejected from. Waits for isAuthReady
 * so the decision is never made against a still-loading permissions snapshot.
 */
function CompanyIndexRedirect() {
  const { nip } = useParams();
  const { isSuperAdmin, permissions, isAuthReady } = useAuth();
  if (!isAuthReady) return <AuthGateSpinner />;
  if (!nip) return <Navigate to="/" replace />;
  return <Navigate to={landingSectionFor(isSuperAdmin, permissions, nip)} replace />;
}

/**
 * Pathless layout gate for the /:nip section routes: renders the child route
 * only when the user's permissions grant `module` for this company —
 * otherwise redirects to their landing section, so deep-linking (or a stale
 * bookmark) can never mount a page whose module the user lacks (the reported
 * bug: a `pracownik` opening /:nip/invoices saw the full filter bar + tag
 * chips). `section` is the route path this gate protects — when even the
 * landing fallback resolves to it (a misconfigured account with no
 * accessible section at all), render an access-denied notice instead of
 * redirect-looping through it.
 */
function ModuleGatedRoute({ section, module }: { section: string; module: PermissionModule }) {
  const { nip } = useParams();
  const { isSuperAdmin, permissions, isAuthReady } = useAuth();
  // A pathless layout route interposes its own <Outlet> between AppShell and
  // the page — the AppShell context ({nip, ...}) MUST be forwarded, or every
  // gated page's useOutletContext() returns undefined (prod incident
  // 2026-07-23: black screen, "Cannot destructure property 'nip'").
  const shellContext = useOutletContext();
  if (!isAuthReady) return <AuthGateSpinner />;
  if (!nip || isSuperAdmin || hasModuleAccess(permissions, nip, module)) {
    return <Outlet context={shellContext} />;
  }
  const landing = landingSectionFor(isSuperAdmin, permissions, nip);
  if (landing === section) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <p className="text-muted-foreground text-sm">{t('errors.accessDenied')}</p>
      </div>
    );
  }
  return <Navigate to={`/${nip}/${landing}`} replace />;
}

/**
 * The invoice-detail route lives OUTSIDE AppShell (no sidebar), so it needs
 * its own gate: the page serves both purchase and sale invoices (backend
 * picks the module by direction), so either 'expense' or 'sales' access
 * mounts it; a module-less user (e.g. `pracownik`) is sent to their landing
 * section instead of a dead full-screen error page.
 */
function InvoiceDetailGate() {
  const { nip } = useParams();
  const { isSuperAdmin, permissions, isAuthReady } = useAuth();
  if (!isAuthReady) return <AuthGateSpinner />;
  if (
    !nip ||
    isSuperAdmin ||
    hasModuleAccess(permissions, nip, 'expense') ||
    hasModuleAccess(permissions, nip, 'sales')
  ) {
    return <InvoiceDetailPage />;
  }
  return <Navigate to={`/${nip}/${landingSectionFor(isSuperAdmin, permissions, nip)}`} replace />;
}

function App() {
  // isAuthenticated covers BOTH session types: MSAL (Entra) account or local
  // (email + password) JWT session. The MSAL AuthenticatedTemplate /
  // UnauthenticatedTemplate components only know about MSAL accounts, so they
  // were replaced with this dual-session gating.
  const { isSuperAdmin, isAuthenticated, isAuthReady, permissions } = useAuth();
  // /admin/users is also reachable by a company admin (scoped to their own
  // companies server-side) — /admin/companies stays superadmin-only.
  const canManageUsers = isSuperAdmin || hasCompanyAdminRole(permissions);

  return (
    <BrowserRouter>
      <AuthSessionProvider>
        <SessionExpiredModal />
        <VersionUpdateNotifier />
        <div className="min-h-screen">
          {isAuthenticated ? (
            <Routes>
              <Route index element={<RootRedirect />} />
              <Route path=":nip/invoices/:invoiceId" element={<InvoiceDetailGate />} />
              <Route
                path="admin/companies"
                element={
                  <GatedRoute ready={isAuthReady} allowed={isSuperAdmin}>
                    <AdminLayout />
                  </GatedRoute>
                }
              >
                <Route index element={<CompanyManagement />} />
              </Route>
              <Route
                path="admin/users"
                element={
                  <GatedRoute ready={isAuthReady} allowed={canManageUsers}>
                    <AdminLayout />
                  </GatedRoute>
                }
              >
                <Route index element={<UserManagement />} />
              </Route>
              <Route
                path="admin/api-apps"
                element={
                  <GatedRoute ready={isAuthReady} allowed={isSuperAdmin}>
                    <AdminLayout />
                  </GatedRoute>
                }
              >
                <Route index element={<ApiApplications />} />
              </Route>
              <Route path=":nip" element={<AppShell />}>
                <Route index element={<CompanyIndexRedirect />} />
                <Route element={<ModuleGatedRoute section="dashboard" module="dashboard" />}>
                  <Route path="dashboard" element={<DashboardPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="invoices" module="expense" />}>
                  <Route path="invoices" element={<InvoiceListPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="sales" module="sales" />}>
                  <Route path="sales" element={<SalesPage />} />
                  <Route path="sales/report" element={<SalesReportPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="fiscal" module="fiscal" />}>
                  <Route path="fiscal" element={<FiscalLayout />}>
                    <Route index element={<FiscalDashboardPage />} />
                    <Route path="invoices" element={<FiscalInvoicesPage />} />
                    <Route path="receipts" element={<FiscalReceiptsPage />} />
                    <Route path="printers" element={<FiscalPrintersPage />} />
                    <Route path="settings" element={<FiscalSettingsPage />} />
                  </Route>
                </Route>
                <Route element={<ModuleGatedRoute section="projects" module="projects" />}>
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="projects/:projectId" element={<ProjectDetailPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="contractors" module="contractors" />}>
                  <Route path="contractors" element={<ContractorsPage />} />
                  <Route path="contractors/:contractorId" element={<ContractorDetailPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="crm" module="crm" />}>
                  <Route path="crm" element={<CrmLayout />}>
                    <Route index element={<LeadQueue />} />
                    <Route path="klienci" element={<AccountList />} />
                    <Route path="klienci/:accountId" element={<AccountDetail />} />
                    <Route path="szanse" element={<DealList />} />
                  </Route>
                </Route>
                <Route element={<ModuleGatedRoute section="magazyn" module="magazyn" />}>
                  <Route path="magazyn" element={<MagazynLayout />}>
                    <Route index element={<WzList />} />
                    <Route path="wz/new" element={<WzEditor />} />
                    <Route path="wz/:wzId" element={<WzEditor />} />
                    <Route path="pozycje" element={<PozycjeCatalog />} />
                  </Route>
                </Route>
                <Route element={<ModuleGatedRoute section="mileage" module="mileage" />}>
                  <Route path="mileage" element={<MileagePage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="employees" module="employees" />}>
                  <Route path="employees" element={<EmployeesPage />} />
                  <Route path="employees/cost-report" element={<EmployeeCostReportPage />} />
                  <Route path="employees/:employeeId" element={<EmployeeDetailPage />} />
                </Route>
                <Route element={<ModuleGatedRoute section="tags" module="tags" />}>
                  <Route path="tags" element={<TagManagement />} />
                </Route>
                <Route element={<ModuleGatedRoute section="hr" module="hr" />}>
                  <Route path="hr" element={<HrPage />} />
                  <Route path="hr/invoices" element={<HrMyInvoicesPage />} />
                  <Route path="hr/reimbursements" element={<HrReimbursementsPage />} />
                  <Route path="hr/my-data" element={<HrMyDataPage />} />
                  <Route path="hr/documents" element={<HrDocumentsPage />} />
                  <Route path="hr/team" element={<HrTeamPage />} />
                  <Route path="hr/admin" element={<HrAdminPage />} />
                </Route>
                {/* Documents is role-gated (admin/hr/ksiegowy), not module-gated —
                    the sidebar hides it and the backend filters its content, so it
                    stays outside the module gates. */}
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="documents/templates/:templateId" element={<TemplateEditorPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="*" element={<LoginPage />} />
            </Routes>
          )}
        </div>
      </AuthSessionProvider>
    </BrowserRouter>
  );
}

export default App;
